// USD -> VND rate for the quote, tried in order:
//   1. Vietcombank's own API (sell rate; walks back a few days for holidays)
//   2. vnappmob (the previous source, also VCB sell; kept in case it recovers)
//   3. open.er-api.com (market mid rate, last online resort)
//   4. the last rate fetched successfully on this browser (marked stale)
// Every source must return a plausible rate, otherwise the next one is tried.

export const RATE_CACHE_KEY = "quote-usd-vnd-rate";
const TIMEOUT_MS = 8000;
const VCB_LOOKBACK_DAYS = 3;

export const isPlausibleRate = (rate) =>
  Number.isFinite(rate) && rate > 10000 && rate < 100000;

const pad = (n) => String(n).padStart(2, "0");
export const isoDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

async function fetchJson(fetchImpl, url, init = {}) {
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller && setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetchImpl(url, {
      ...init,
      headers: { Accept: "application/json", ...(init.headers || {}) },
      signal: controller ? controller.signal : undefined,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status} ${url}`);
    return await response.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
}

const toRate = (value) => Number(String(value).replace(/,/g, ""));

async function vietcombank(fetchImpl, now) {
  for (let back = 0; back <= VCB_LOOKBACK_DAYS; back++) {
    const day = new Date(now);
    day.setDate(day.getDate() - back);
    const data = await fetchJson(
      fetchImpl,
      `https://www.vietcombank.com.vn/api/exchangerates?date=${isoDate(day)}`
    );
    const usd = (data.Data || []).find((r) => r.currencyCode === "USD");
    const rate = usd && toRate(usd.sell);
    if (isPlausibleRate(rate)) {
      return { rate, date: (data.Date || isoDate(day)).slice(0, 10) };
    }
  }
  throw new Error("Vietcombank: no USD rate");
}

async function vnappmob(fetchImpl, now) {
  const key = await fetchJson(
    fetchImpl,
    "https://vapi.vnappmob.com/api/request_api_key?scope=exchange_rate"
  );
  const data = await fetchJson(
    fetchImpl,
    "https://api.vnappmob.com/api/v2/exchange_rate/vcb",
    { headers: { Authorization: `Bearer ${key.results}` } }
  );
  const usd = (data.results || []).find((r) => r.currency === "USD");
  const rate = usd && toRate(usd.sell);
  if (!isPlausibleRate(rate)) throw new Error("vnappmob: no USD rate");
  return { rate, date: isoDate(now) };
}

async function openErApi(fetchImpl) {
  const data = await fetchJson(
    fetchImpl,
    "https://open.er-api.com/v6/latest/USD"
  );
  const rate = data.rates && Number(data.rates.VND);
  if (!isPlausibleRate(rate)) throw new Error("open.er-api: no VND rate");
  const date = data.time_last_update_unix
    ? isoDate(new Date(data.time_last_update_unix * 1000))
    : null;
  return { rate: Math.round(rate), date };
}

export const RATE_SOURCES = [
  { source: "Vietcombank (bán ra)", load: vietcombank },
  { source: "Vietcombank qua vnappmob", load: vnappmob },
  { source: "open.er-api.com (thị trường)", load: openErApi },
];

function readCache(storage) {
  try {
    const cached = JSON.parse(storage.getItem(RATE_CACHE_KEY));
    return cached && isPlausibleRate(cached.rate) ? cached : null;
  } catch (e) {
    return null;
  }
}

function writeCache(storage, value) {
  try {
    storage.setItem(RATE_CACHE_KEY, JSON.stringify(value));
  } catch (e) {
    // private mode / storage full: the rate still works for this session
  }
}

/**
 * @returns {Promise<{rate, source, date, stale}|null>} null when no source
 *   and no cached rate is available (the user has to type the rate).
 */
export async function getUsdVndRate({
  fetchImpl = (url, init) => fetch(url, init),
  storage = typeof localStorage !== "undefined" ? localStorage : null,
  now = new Date(),
  sources = RATE_SOURCES,
} = {}) {
  for (const { source, load } of sources) {
    try {
      const { rate, date } = await load(fetchImpl, now);
      const result = { rate, source, date, stale: false };
      if (storage) writeCache(storage, result);
      return result;
    } catch (e) {
      console.warn(`Exchange rate: ${source} failed`, e);
    }
  }
  const cached = storage && readCache(storage);
  return cached ? { ...cached, stale: true } : null;
}
